/**
 * The dispatcher's telephone as the office arranged it: which one rings, which desk, and
 * which call is theirs right now.
 *
 * The browser telephone (`useSoftphone`) knows about its own SIP calls and nothing else. A
 * dispatcher answering on a desk phone has no SIP call in the browser at all, and still
 * needs the console to know who they are talking to — to put them on hold, to park them for
 * a car. So "my call" is worked out here from the realtime events, by the telephones that
 * are mine: the browser's extension and the desk phone's. The server sends who took each
 * call precisely so each console need not guess.
 *
 * Which desk this PC is at is remembered by the PC — a per-browser convenience, like the
 * headset — and told to the server on every load, because the server is what rings the
 * desk phone for whoever is sitting there.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError, request } from '@taxi/api-client';

import { callsApi, type CallSummary } from '../calls/api';
import { useDispatcherHub, type DeskTakenOver } from '../realtime/useDispatcherHub';
import { telephoneApi, type AnswerOn, type MyTelephone } from './api';

/** The inbound call this dispatcher is talking to now, on either of their telephones. */
export interface MyCall {
  callId: string;
  extension: string;
  from: string | null;
  name: string | null;
  since: number;
}

export interface Telephone {
  /** Null until loaded, and for good at a firm without telephony. */
  mine: MyTelephone | null;
  refresh: () => Promise<void>;

  myCall: MyCall | null;

  /**
   * The numbers of everybody on hold on the PBX right now — parked by a dispatcher, or
   * waiting for a car. An order's card offers to ring its customer, and must not while
   * they are already on a line listening to music.
   */
  onHold: string[];

  /** Answering on the desk phone rather than in the browser. */
  onDesk: boolean;

  /** Where desks are chosen and this PC has not said which it is at. */
  askForDesk: boolean;
  chooseDesk: (deskId: string | null) => Promise<void>;

  chooseAnswerOn: (answerOn: AnswerOn, ringOtherWhenUnavailable: boolean) => Promise<void>;

  /** Somebody sat down at this dispatcher's desk. */
  takenOver: DeskTakenOver | null;
  acknowledgeTakeover: () => void;

  /** Gone home: leaves the desk, where desks are chosen. The PC still remembers it. */
  leaveDesk: () => Promise<void>;
}

const DESK_KEY = 'taxi.console.desk';

/** Whatever this PC remembers: a desk's id, `none`, or nothing yet. */
function rememberedDesk(): string | null {
  try {
    return globalThis.localStorage?.getItem(DESK_KEY) ?? null;
  } catch {
    return null;
  }
}

function rememberDesk(value: string | null): void {
  try {
    if (value === null) globalThis.localStorage?.removeItem(DESK_KEY);
    else globalThis.localStorage?.setItem(DESK_KEY, value);
  } catch {
    // Storage refused. The console asks again next time, which is all that is lost.
  }
}

/**
 * How often the desk phone's reachability is asked again.
 *
 * The PBX notices an unplugged handset within thirteen seconds; half a minute here means a
 * dispatcher is told within one more glance at the screen, for one small request.
 */
const RefreshEvery = 30_000;

export function useTelephone(browserExtension: string | null): Telephone {
  const [mine, setMine] = useState<MyTelephone | null>(null);
  const [myCall, setMyCall] = useState<MyCall | null>(null);
  const [askForDesk, setAskForDesk] = useState(false);
  const [takenOver, setTakenOver] = useState<DeskTakenOver | null>(null);
  const [onHold, setOnHold] = useState<string[]>([]);

  // Both lists, because they are two different holds: parked is a dispatcher's own doing,
  // held is the firm's hold flow. Each failing on its own leaves the other's answer intact.
  const loadOnHold = useCallback(async () => {
    const [parked, held] = await Promise.all([
      telephoneApi.parked().catch((): CallSummary[] => []),
      callsApi.held().then((all) => all.calls).catch((): CallSummary[] => []),
    ]);

    setOnHold(
      [...parked, ...held]
        .map((call) => call.from)
        .filter((number): number is string => number !== null),
    );
  }, []);

  /** Who each ringing call is, from the pops, so a call taken can be named without asking. */
  const pops = useRef(new Map<string, { from: string | null; name: string | null }>());

  const load = useCallback(async (): Promise<MyTelephone | null> => {
    try {
      const loaded = await telephoneApi.me();
      setMine(loaded);
      return loaded;
    } catch (failure) {
      // A firm that did not buy telephony, or has none configured: no telephone, no
      // desk, and nothing to say about it.
      if (!(failure instanceof ApiError)) throw failure;
      setMine(null);
      return null;
    }
  }, []);

  const refresh = useCallback(async () => {
    await load();
  }, [load]);

  // On load, the PC says which desk it is at. Remembered as `none` is a choice too, and
  // honoured; remembered as a desk that has since been removed is forgotten and asked for.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const loaded = await load().catch(() => null);

      if (cancelled || loaded === null) return;

      void loadOnHold();

      if (loaded.deskPhonesBelongTo !== 'Desk' || loaded.desks.length === 0) return;

      const remembered = rememberedDesk();

      if (remembered === null) {
        setAskForDesk(true);
        return;
      }

      if (remembered === 'none') {
        if (loaded.desk !== null) {
          await telephoneApi.sitAt(null).catch(() => undefined);
          if (!cancelled) await load();
        }
        return;
      }

      if (!loaded.desks.some((desk) => desk.id === remembered)) {
        rememberDesk(null);
        setAskForDesk(true);
        return;
      }

      if (loaded.desk?.id !== remembered) {
        await telephoneApi.sitAt(remembered).catch(() => undefined);
        if (!cancelled) await load();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [load, loadOnHold]);

  useEffect(() => {
    const timer = globalThis.setInterval(() => void load().catch(() => undefined), RefreshEvery);

    return () => globalThis.clearInterval(timer);
  }, [load]);

  const deskExtensions = mine?.desk?.extensions;

  // The registered one when there is one; before the browser has registered, the one the
  // server says was issued.
  const browser = browserExtension ?? mine?.browserExtension ?? null;

  /** Whether a call taken on this extension was taken by this dispatcher: any phone on their desk. */
  const isMine = useCallback(
    (extension: string | null) =>
      extension !== null &&
      (extension === browser || (deskExtensions?.includes(extension) ?? false)),
    [browser, deskExtensions],
  );

  useDispatcherHub({
    onCallRinging: (call) => {
      pops.current.set(call.callId, { from: call.from, name: call.customerName });

      // A handful of recent calls is all this is for; it must not grow through a shift.
      if (pops.current.size > 50) {
        const oldest = pops.current.keys().next().value;
        if (oldest !== undefined) pops.current.delete(oldest);
      }
    },

    onCallTaken: (taken) => {
      if (isMine(taken.extension)) {
        const known = pops.current.get(taken.callId);

        setMyCall({
          callId: taken.callId,
          extension: taken.extension!,
          from: known?.from ?? null,
          name: known?.name ?? null,
          since: Date.now(),
        });

        // Taken back off hold, or rung through: no pop to name them, so ask once.
        if (known === undefined) {
          void request<CallSummary>(`/api/calls/${taken.callId}`)
            .then((call) =>
              setMyCall((current) =>
                current?.callId === call.id ? { ...current, from: call.from } : current,
              ),
            )
            .catch(() => undefined);
        }

        return;
      }

      // Somebody else has it now.
      setMyCall((current) => (current?.callId === taken.callId ? null : current));
    },

    onCallParkedChanged: (change) => {
      if (change.parked) {
        setMyCall((current) => (current?.callId === change.callId ? null : current));
      }

      void loadOnHold();
    },

    onCallHeldChanged: (change) => {
      if (change.held) {
        setMyCall((current) => (current?.callId === change.callId ? null : current));
      }

      void loadOnHold();
    },

    onCallFinished: (finished) => {
      pops.current.delete(finished.callId);
      setMyCall((current) => (current?.callId === finished.callId ? null : current));

      // A caller who hung up while on hold leaves neither list by any other event.
      if (onHold.length > 0) void loadOnHold();
    },

    onDeskTakenOver: (notice) => {
      // This PC is no longer at that desk as far as the office is concerned. Forgotten, so
      // the next load asks rather than silently taking it back from whoever sat down.
      rememberDesk(null);
      setTakenOver(notice);
      void load().catch(() => undefined);
    },

    onReconnected: () => void load().catch(() => undefined),
  });

  const chooseDesk = useCallback(
    async (deskId: string | null) => {
      await telephoneApi.sitAt(deskId);
      rememberDesk(deskId ?? 'none');
      setAskForDesk(false);
      setTakenOver(null);
      await load();
    },
    [load],
  );

  const chooseAnswerOn = useCallback(
    async (answerOn: AnswerOn, ringOtherWhenUnavailable: boolean) => {
      await telephoneApi.saveMine(answerOn, ringOtherWhenUnavailable);
      await load();
    },
    [load],
  );

  const leaveDesk = useCallback(async () => {
    if (mine?.deskPhonesBelongTo !== 'Desk' || mine.desk === null) return;

    await telephoneApi.sitAt(null).catch(() => undefined);
  }, [mine]);

  const acknowledgeTakeover = useCallback(() => setTakenOver(null), []);

  return {
    mine,
    refresh,
    myCall,
    onHold,
    onDesk: mine !== null && mine.answerOn === 'DeskPhone' && mine.desk !== null,
    askForDesk,
    chooseDesk,
    chooseAnswerOn,
    takenOver,
    acknowledgeTakeover,
    leaveDesk,
  };
}
